//@ts-ignore
import React, { useEffect, useRef, useState } from 'react';
import Pannellum from './Pannelum';
import { PanoramaOverlay } from './styled';

export const Panorama = ({ panoramas, awsUrl, panoPose, ...props }: any) => {
  const [state, setState] = useState({
    loaded: false,
    current: 0
  });
  const [pannellums, setPannellums] = useState<any>([]);
  const [scenes, setScenes] = useState<any>({});
  const { hfov, pitch, yaw } = panoPose;

  const panoPosePrev = useRef({ h: hfov, p: pitch, y: yaw });
  const scenesLength = Object.keys(scenes).length;

  const onPanoLoaded = () => {
    setState((prevState) => ({ ...prevState, loaded: true }));
  };

  const showLoading = (checkGuest?: boolean) => {
    if (checkGuest) {
      if (props.isChatGuest) {
        setState((prevState) => ({ ...prevState, loaded: false }));
      }
    } else {
      setState((prevState) => ({ ...prevState, loaded: false }));
    }
  };

  const loadSelf = (scene: string) => {
    pannellums[0].loadScene({
      scene,
      pitch: scenes[scene].startPitch,
      yaw: scenes[scene].startYaw,
      hfov: scenes[scene].hfov
    });
  };

  const getPanoHotspots = (scene: string) => {
    if (!scenes[scene] || !scenes[scene].hotspots) return [];
    const hotspots: any = [];
    scenes[scene].hotspots.forEach((h: any) => {
      if (h.type === 'panorama') {
        hotspots.push({
          id: `h-${h.id}`,
          scene: h.scene,
          pitch: h.pitch,
          yaw: h.yaw
        });
      }
    });
    return hotspots;
  };

  const loadCurrentScenes = (playerScene: string, x: number, loadCurrent = false) => {
    showLoading(true);
    const updatedPannellums = pannellums.map((pannellum) => {
      pannellums[x].active = true;
      return pannellum;
    });
    setPannellums(updatedPannellums);

    props.onSceneChange(scenes[playerScene]);

    const { startPitch: pitch, startYaw: yaw, hfov } = scenes[playerScene];

    if (loadCurrent) {
      pannellums[x].addScene(scenes[playerScene]);
      pannellums[x].loadScene({
        scene: playerScene,
        pitch,
        yaw,
        hfov
      });
      pannellums[x].setNorthOffset(scenes[playerScene].northYaw);
    } else {
      const animationTime = 1000;
      pannellums[x].lookAt({
        pitch,
        yaw,
        bounds: hfov,
        time: animationTime
      });
    }

    setState((prevState) => ({ ...prevState, current: x }));

    // max 4 hotspots -> forward, backward, left, right
    const panoHotspots = getPanoHotspots(playerScene);
    const currentScene = scenes[pannellums[x].getScene()];

    if (!currentScene) return;
    props.markMinimap(currentScene);

    if (!props.transition) return;

    // TODO: improve performence by not loading the same panorama twice
    panoHotspots.forEach((h: any, i: number) => {
      const j = (x + i + 1) % 4; // find all the next pannellum instances
      const scene = scenes[h.scene];
      if (!scene) return;
      pannellums[j].active = false;
      pannellums[x].hotspotsMap[h.id] = pannellums[j];

      const p =
        scene.northPitch && currentScene.northPitch
          ? scene.northPitch - (currentScene.northPitch - h.pitch)
          : null;
      const y =
        scene.northYaw && currentScene.northYaw
          ? scene.northYaw - (currentScene.northYaw - h.yaw)
          : null;
      pannellums[j].addScene(scene);
      pannellums[j].loadScene({
        scene: scene.name,
        pitch: p,
        yaw: y,
        hfov: scene.hfov
      });
      pannellums[j].setNorthOffset(scene.northYaw);
    });
  };

  useEffect(() => {
    const updatedScenes: any = {};

    panoramas.forEach((p: any) => {
      updatedScenes[p.name] = p;
    });

    const options = {
      loadCurrentScenes: loadCurrentScenes,
      updatePanoRotation: props.updatePanoRotation,
      openHotspot: props.openHotspot,
      onLoaded: onPanoLoaded,
      showLoading: showLoading,
      loadSelf: loadSelf,
      transition: props.transition
    };

    const firstPannellum = new Pannellum({
      id: 1,
      autoLoad: false,
      AWS_PATH: awsUrl,
      ...options
    });
    if (!options.transition) {
      setPannellums([firstPannellum]);
    } else {
      const updatedPannellums = [
        firstPannellum,
        new Pannellum({
          id: 2,
          AWS_PATH: awsUrl,
          ...options
        }),
        new Pannellum({
          id: 3,
          AWS_PATH: awsUrl,
          ...options
        }),
        new Pannellum({
          id: 4,
          AWS_PATH: awsUrl,
          ...options
        })
      ];
      setPannellums(updatedPannellums);
    }

    setScenes(updatedScenes);

    return () => {
      setPannellums([]);
      setScenes({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (panoramas.length === 0 || pannellums.length === 0 || scenesLength === 0) {
      return;
    }

    let firstScene: any;

    // override default if activePano exists
    panoramas.forEach((p: any) => {
      if (props.activePano && props.activePano === p.id) {
        firstScene = p;
      }
    });

    if (!props.transition) {
      // eslint-disable-next-line array-callback-return
      Object.keys(scenes).map((scene: string) => {
        pannellums[0].addScene(scenes[scene]);
      });
    }

    if (firstScene) {
      loadCurrentScenes(firstScene.name, 0, true);

      const { pitch, yaw, hfov } = panoPose;
      if (pitch || yaw || hfov) {
        pannellums[0].setPose(panoPose);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pannellums.length, panoramas, scenesLength]);

  useEffect(() => {
    if (pannellums.length > 0) {
      if (props.activePano !== state.current) {
        panoramas.forEach((p: any) => {
          if (p.id === props.activePano && scenes[p.name]) {
            loadCurrentScenes(p.name, state.current, true);
          }
        });
      }
      const { h, p, y } = panoPosePrev.current;
      if (hfov !== h || pitch !== p || yaw !== y) {
        pannellums[state.current].setPose(panoPose);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.current, props.activePano, panoPose]);

  return (
    <PanoramaOverlay>
      <div style={{ height: '100%', width: '100%' }}>
        <div id="panorama-player-1" className="panorama show" />
        <div id="panorama-player-2" className="panorama" />
        <div id="panorama-player-3" className="panorama" />
        <div id="panorama-player-4" className="panorama" />
      </div>
      {!state.loaded && (
        <div className="loadingDiv">
          {/* <Loader type="Oval" color="#ea475c" height={100} width={100} /> */}
        </div>
      )}
    </PanoramaOverlay>
  );
};
